import { inject, Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { DocumentNode, GraphQLFormattedError } from 'graphql';
import { lastValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { RoleOperationError, throwIfGraphQLErrors } from '../identity-graphql-error';
import { GetPermissionsResponse, PermissionCatalogDto } from './permission-catalog.interface';

/** Fetches the permission catalog (paginated on the server). */
const GET_PERMISSIONS: DocumentNode = gql`
  query GetPermissions($first: Int, $after: String) {
    GetPermissions(first: $first, after: $after) {
      nodes {
        id
        area
        target
        action
        displayName
        enforcementKey
        description
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

/**
 * Service for loading the platform permission catalog via GetPermissions.
 *
 * Query name is derived from the backend GraphQL schema (Tenant.Web.Graphql.Queries.Identity).
 */
@Injectable()
export class PermissionCatalogService {
  /** Page size when auto-paginating the catalog. */
  private static readonly CATALOG_PAGE_SIZE: number = 50;

  /** Apollo client for GQL communication. */
  private readonly apollo: Apollo = inject(Apollo);

  /**
   * Fetches the full permission catalog (auto-paginates).
   * @returns Promise of all catalog permission rows.
   */
  public async getPermissionsCatalog(): Promise<Array<PermissionCatalogDto>> {
    const allPermissions: Array<PermissionCatalogDto> = new Array<PermissionCatalogDto>();
    let hasNextPage: boolean = true;
    let after: string | undefined;

    while (hasNextPage) {
      const page: {
        nodes: Array<PermissionCatalogDto>;
        hasNextPage: boolean;
        endCursor?: string;
      } = await lastValueFrom(
        this.apollo
          .query<GetPermissionsResponse>({
            query: GET_PERMISSIONS,
            variables: { first: PermissionCatalogService.CATALOG_PAGE_SIZE, after },
            fetchPolicy: 'network-only'
          })
          .pipe(
            map(
              (response: {
                data?: GetPermissionsResponse;
                errors?: ReadonlyArray<GraphQLFormattedError>;
              }): {
                nodes: Array<PermissionCatalogDto>;
                hasNextPage: boolean;
                endCursor?: string;
              } => {
                throwIfGraphQLErrors(response.errors, 'Permission catalog operation failed.');

                const connection: GetPermissionsResponse['GetPermissions'] | undefined =
                  response.data?.GetPermissions;
                if (!connection) {
                  throw new RoleOperationError(false, 'GetPermissions returned no data.');
                }

                return {
                  nodes: connection.nodes ?? new Array<PermissionCatalogDto>(),
                  hasNextPage: connection.pageInfo?.hasNextPage ?? false,
                  endCursor: connection.pageInfo?.endCursor ?? undefined
                };
              }
            )
          )
      );

      allPermissions.push(...page.nodes);
      hasNextPage = page.hasNextPage;
      after = page.endCursor;
    }

    return allPermissions;
  }
}
